import React from "react";
import styles from "./FunnelStageKfaCard.module.css";

const TITLE_META = [
  { match: (t) => t.includes("indexing"), subtitle: "Remove crawl friction and sharpen technical visibility.", badge: "INDEX" },
  { match: (t) => t.includes("homepage"), subtitle: "Clarify first-impression positioning and intent routing.", badge: "HOME" },
  { match: (t) => t.includes("pricing"), subtitle: "Tighten price architecture and bundle logic.", badge: "PRICE" },
  { match: (t) => t.includes("instagram") || t.includes("facebook"), subtitle: "Narrative-driven social cadence that compounds reach.", badge: "SOCIAL" },
  { match: (t) => t.includes("paid ad"), subtitle: "Controlled acquisition tests to validate scale levers.", badge: "PAID" },
  { match: (t) => t.includes("product intelligence"), subtitle: "Inventory truth and demand forecasting clarity.", badge: "SUPPLY" },
  { match: (t) => t.includes("seo"), subtitle: "Compounding search visibility through intent-led content.", badge: "SEO" },
  { match: (t) => t.includes("pdp"), subtitle: "Make product detail pages convert with clarity and proof.", badge: "PDP" },
  { match: (t) => t.includes("email"), subtitle: "Retention automation that moves buyers toward repeat.", badge: "EMAIL" },
  { match: (t) => t.includes("order economics"), subtitle: "Lift AOV and margin with smarter basket economics.", badge: "AOV" },
  { match: (t) => t.includes("customer economics"), subtitle: "Increase LTV with loyalty and smart segmentation.", badge: "LTV" },
  { match: (t) => t.includes("leakage"), subtitle: "Seal revenue leaks across cart and checkout.", badge: "RECOVERY" },
];

const cleanLines = (block = "") =>
  String(block || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

const parseFunnelBlock = (markdown = "") => {
  if (!markdown) return null;
  const lines = cleanLines(markdown);
  if (!lines.length) return null;

  const titleMatch = lines[0].match(/\*\*(.+?)\*\*/);
  const title = titleMatch ? titleMatch[1].trim() : lines[0];
  const rest = titleMatch ? lines.slice(1) : lines;

  const bullets = [];
  const targets = [];
  const summaryLines = [];
  const interpretationLines = [];
  const compoundingLines = [];
  let flywheelCoverage = "";
  let goalState = "";
  let section = "bullets";

  rest.forEach((line) => {
    const lower = line.toLowerCase();
    if (lower.startsWith("flywheel coverage:")) {
      flywheelCoverage = line.replace(/^flywheel coverage:\s*/i, "").trim();
      return;
    }
    if (lower.startsWith("goal state:")) {
      goalState = line.replace(/^goal state:\s*/i, "").trim();
      return;
    }
    if (lower === "summary") {
      section = "summary";
      return;
    }
    if (lower === "interpretation") {
      section = "interpretation";
      return;
    }
    if (lower === "targets") {
      section = "targets";
      return;
    }
    if (lower === "why this compounds") {
      section = "compounding";
      return;
    }

    const cleaned = line.replace(/^[-*]\s*/, "");
    if (section === "bullets") bullets.push(cleaned);
    if (section === "targets") targets.push(cleaned);
    if (section === "summary") summaryLines.push(line);
    if (section === "interpretation") interpretationLines.push(line);
    if (section === "compounding") compoundingLines.push(line);
  });

  return {
    title,
    bullets: bullets.filter(Boolean),
    targets: targets.filter(Boolean),
    summary: summaryLines.join(" ").trim(),
    interpretation: interpretationLines.join(" ").trim(),
    compounding: compoundingLines.join(" ").trim(),
    flywheelCoverage,
    goalState,
  };
};

const resolveMeta = (title = "") => {
  const t = title.toLowerCase();
  const match = TITLE_META.find((item) => item.match(t));
  return (
    match || {
      subtitle: "Strategic execution block for the funnel OS.",
      badge: "STRATEGY",
    }
  );
};

const FunnelStageKfaCard = ({ markdown }) => {
  const parsed = parseFunnelBlock(markdown);
  if (!parsed) return null;

  const meta = resolveMeta(parsed.title);

  return (
    <div className={styles.card}>
      <div className={styles.hero}>
        <div>
          <h4 className={styles.heroTitle}>{parsed.title}</h4>
          <p className={styles.heroSubtitle}>{meta.subtitle}</p>
        </div>
        <div className={styles.heroBadge}>{meta.badge}</div>
      </div>

      {parsed.bullets.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Actions</div>
          {parsed.bullets.map((item, idx) => (
            <div key={`${item}-${idx}`} className={styles.row}>
              <span className={styles.dot} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      ) : null}

      {parsed.flywheelCoverage ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Flywheel Coverage</div>
          <p className={styles.sectionText}>{parsed.flywheelCoverage}</p>
        </div>
      ) : null}

      {parsed.goalState ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Goal State</div>
          <p className={styles.sectionText}>{parsed.goalState}</p>
        </div>
      ) : null}

      {parsed.targets.length > 0 ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Targets</div>
          {parsed.targets.map((item, idx) => (
            <div key={`${item}-${idx}`} className={styles.row}>
              <span className={styles.dotAlt} />
              <span>{item}</span>
            </div>
          ))}
        </div>
      ) : null}

      {parsed.compounding ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Why This Compounds</div>
          <p className={styles.sectionText}>{parsed.compounding}</p>
        </div>
      ) : null}

      {parsed.summary ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Summary</div>
          <p className={styles.sectionText}>{parsed.summary}</p>
        </div>
      ) : null}

      {parsed.interpretation ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>Interpretation</div>
          <p className={styles.sectionText}>{parsed.interpretation}</p>
        </div>
      ) : null}
    </div>
  );
};

export default FunnelStageKfaCard;
